import { client } from '~publish/legacy/apollo-client'
import { graphql } from '~publish/graphql'

import type { HashtagGroup } from './types'

/**
 * Account-actor query that replaces the legacy `getHashtagGroups` RPC /
 * `GET /1/snippet_groups.json` for Publish Hashtag Manager.
 * Backend ships as paginated Snippet GraphQL (CHAN-12104), not HashtagGroup naming.
 * Page through all results so the Redux list matches the legacy full payload.
 */
export const GetSnippets = graphql(/* GraphQL */ `
  query GetSnippets($input: SnippetsInput!, $first: Int, $after: String) {
    snippets(input: $input, first: $first, after: $after) {
      edges {
        node {
          id
          name
          text
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

const PAGE_SIZE = 100

export function mapSnippetToHashtagGroup(snippet: {
  id: string
  name: string
  text: string
}): HashtagGroup {
  return {
    _id: snippet.id,
    id: snippet.id,
    name: snippet.name,
    text: snippet.text,
  }
}

async function fetchAllSnippetPages(args: {
  organizationId: string
  after: string | null
  accumulated: HashtagGroup[]
}): Promise<HashtagGroup[]> {
  const { data } = await client.query({
    query: GetSnippets,
    variables: {
      input: { organizationId: args.organizationId },
      first: PAGE_SIZE,
      after: args.after,
    },
    fetchPolicy: 'network-only',
    // Overlapping refreshes must not share one in-flight response; the
    // generation guard alone cannot tell a shared stale body from a fresh one.
    context: { queryDeduplication: false },
  })

  const connection = data?.snippets
  if (!connection) {
    throw new Error('GetSnippets returned no connection')
  }

  const pageGroups = connection.edges.map((edge) =>
    mapSnippetToHashtagGroup(edge.node),
  )
  const accumulated = [...args.accumulated, ...pageGroups]

  if (!connection.pageInfo.hasNextPage) {
    return accumulated
  }

  const endCursor = connection.pageInfo.endCursor
  if (!endCursor) {
    throw new Error('GetSnippets reported hasNextPage without an endCursor')
  }

  // Next page depends on this page's cursor; load sequentially.
  return fetchAllSnippetPages({
    organizationId: args.organizationId,
    after: endCursor,
    accumulated,
  })
}

export async function getHashtagGroupsViaGraphQL(args: {
  organizationId: string
}): Promise<HashtagGroup[]> {
  return fetchAllSnippetPages({
    organizationId: args.organizationId,
    after: null,
    accumulated: [],
  })
}
